import { type FC, type PropsWithChildren, useEffect, useState } from 'react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { type Query, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type AppState, Auth0Provider } from '@auth0/auth0-react';
import { useShallow } from 'zustand/react/shallow';
import { datadogRum } from '@datadog/browser-rum';
import { AlertTriangle } from 'lucide-react';
import { type Features, featuresDefaults, useFeatureStore } from '@/hooks/data/feature/useFeatureStore';
import { DuplicateProgressManager } from '@/components/duplicate-progress-manager';
import { GlobalErrorProvider, useGlobalError } from '@/context/GlobalErrorContext';
import { GatedExportProgressManager } from '@/components/export-progress-manager';
import { TermsAcceptanceGuard } from '@/components/terms/terms-acceptance-guard';
import { GallerySelectionProvider } from '@/context/GallerySelectionContext';
import { buildAuth0ProviderProps, buildLoginRedirectUrl } from '@/lib/auth0';
import { useStaleViewStore } from '@/hooks/websocket/useStaleViewStore';
import { shouldSuppressGlobalErrorToast } from '@/lib/queryErrorToast';
import { ThumbnailSizeCssVar } from '@/hooks/useThumbnailSizeCssVar';
import { useTenantStore } from '@/hooks/data/tenant/useTenantStore';
import { AuthErrorWall } from '@/components/walls/auth-error-wall';
import { DragAndDropProvider } from '@/context/DragAndDropContext';
import { CommandContextProvider } from '@/context/CommandContext';
import { TypesenseProvider } from '@/context/TypesenseContext';
import { INTERNAL_V1, getApiPort } from '@/hooks/damRequest';
import { TreeContextProvider } from '@/context/TreeContext';
import { HistoryProvider } from '@/context/HistoryContext';
import { messages as i18nMessages } from '@/i18n/messages';
import { TooltipProvider } from '@/components/ui/tooltip';
import { GuestWall } from '@/components/walls/guest-wall';
import { AuthWall } from '@/components/walls/auth-wall';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { ToastVariant } from '@/components/ui/toast';
import { ModalHost } from '@/components/modal-host';
import { NotFound } from '@/components/not-found';
import { Toaster } from '@/components/ui/toaster';
import { toast } from '@/components/ui/use-toast';
import { AppI18nProvider } from '@/i18n/provider';
import { sanitizeRedirectUrl } from '@/lib/utils';
import { type TenantData } from '@/types/tenant';
import { errorProd, log } from '@/utilities/log';
import { Loading } from '@/components/loading';
import '@/styles/globals.css';
import { useTranslations } from 'next-intl';
import { ThemeProvider } from 'next-themes';
import type { AppProps } from 'next/app';
import Router from 'next/router';
import Head from 'next/head';

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/download-sw.js').catch((error) => {
    errorProd(new Error(`Service worker registration failed: ${error.message}`));
  });

  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'DOWNLOAD_ERROR') {
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: i18nMessages.common.error,
        description: i18nMessages.errors.downloadFailed,
      });
    }
  });
}

if (process.env.NODE_ENV === 'production') {
  datadogRum.init({
    applicationId: '2892ff55-6be3-4ced-bc98-bbd922e214d5',
    clientToken: 'pubc3be883b4db3f3f8820d959f3842dda5',
    site: 'datadoghq.com',
    service: 'dam-frontend',
    env: process.env.NEXT_PUBLIC_ENVIRONMENT,
    version: process.env.NEXT_PUBLIC_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [(url) => url.startsWith(location.origin)],
  });
}

// Exported so integration tests can exercise the real onError wiring (suppression,
// 403 redirect, default toast) instead of re-declaring it against a test-only client.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, refetchOnMount: false, retry: false },
    mutations: {
      onError: (error, variables) => {
        const suppressToast =
          shouldSuppressGlobalErrorToast(error) ||
          (typeof variables === 'object' &&
            variables !== null &&
            'suppressGlobalErrorToast' in variables &&
            Boolean((variables as { suppressGlobalErrorToast?: boolean }).suppressGlobalErrorToast));
        if (suppressToast) {
          return;
        }

        errorProd(error);

        toast({
          title: i18nMessages.common.error,
          variant: ToastVariant.DESTRUCTIVE,
          description: error.message ?? i18nMessages.errors.toastDefaultDescription,
        });
      },
    },
  },
  queryCache: new QueryCache({
    // A view the viewer was told to reload has now refetched, so the toolbar indicator must
    // come down for it. Without this the button stays pulsing after they simply navigate to
    // another folder or album, which refetches the listing anyway. (DM-1491)
    onSuccess: (_data, query) => {
      useStaleViewStore.getState().resolveStale(query.queryKey, query.state.dataUpdatedAt);
    },
    onError: (() => {
      let redirecting = false;
      return (error: Error, query: Query<unknown, unknown>) => {
        // Queries that opt out of global handling absorb their own errors
        // (e.g. a thumbnail recovery fetch that 404s for a deleted asset).
        if (query?.meta?.suppressGlobalError) {
          return;
        }
        // Redirect to home when a query returns 403 (user lacks permission for this resource).
        // Queries that own their own 403 UI (e.g. a share whose collaboration was disabled)
        // opt out via meta.suppressForbiddenRedirect and handle the toast themselves.
        if ((error as any)?.status === 403) {
          if (query?.meta?.suppressForbiddenRedirect) {
            return;
          }
          if (!redirecting) {
            redirecting = true;
            toast({
              title: 'Access Denied',
              variant: ToastVariant.DESTRUCTIVE,
              description: 'You do not have permission to access this resource.',
            });
            void Router.push('/').finally(() => {
              redirecting = false;
            });
          }
          return;
        }
        if (shouldSuppressGlobalErrorToast(error)) {
          return;
        }
        errorProd(error);
        toast({
          title: 'Query Error',
          variant: ToastVariant.DESTRUCTIVE,
          description: error.message,
        });
      };
    })(),
  }),
});

/** Exported so integration tests can exercise the real 401 wiring instead of re-declaring it. */
export const GlobalErrorHandler: FC<PropsWithChildren> = ({ children }) => {
  const { errorCode } = useGlobalError();
  const { isPublicSharePage, isSubmitPage } = useCurrentPage();
  const tErrors = useTranslations('errors');

  /** Share and submission pages own their 401 handling; this wall would otherwise
   * replace the page tree first, stranding the user with no path forward. */
  return errorCode === 401 && !isPublicSharePage && !isSubmitPage ? (
    <div className="flex min-h-screen items-center justify-center bg-white text-black dark:bg-neutral-950 dark:text-white">
      <div className="flex flex-col items-center gap-5 rounded-lg bg-yellow-400/20 p-10 dark:bg-yellow-500/10">
        <AlertTriangle className="mr-2 icon-12 stroke-yellow-400 dark:stroke-yellow-500" strokeWidth={1} />
        {tErrors('unauthorizedAccess')}
      </div>
    </div>
  ) : (
    children
  );
};

export default function App({ Component, pageProps }: AppProps) {
  if (!process.env.NEXT_PUBLIC_AUTH_DOMAIN || !process.env.NEXT_PUBLIC_AUTH_CLIENT_ID) {
    throw new Error('Auth0 variables are not set');
  }

  const setTenantData = useTenantStore(useShallow((state) => state.setTenantData));

  const { tenantData } = useTenantStore(useShallow((s) => ({ tenantData: s.tenantData })));
  const { isPublicRoute } = useCurrentPage();

  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const { pathname, search, protocol } = window.location;
    if (pathname === '/login') {
      setRedirecting(true);
      const urlParams = new URLSearchParams(search);
      const organizationName = urlParams.get('organization_name');

      const url = buildLoginRedirectUrl({
        authDomain: process.env.NEXT_PUBLIC_AUTH_DOMAIN!,
        clientId: process.env.NEXT_PUBLIC_AUTH_CLIENT_ID!,
        search,
        protocol,
        organizationName,
        host: window.location.host,
      });
      void Router.replace(url);
    }
  }, []);

  useEffect(() => {
    const fetchTenantData = async () => {
      const hostname = window.location.hostname;

      try {
        const initUrl =
          process.env.NODE_ENV === 'development'
            ? `https://${hostname}:${getApiPort()}${INTERNAL_V1}/init`
            : `${INTERNAL_V1}/init`;

        const res = await fetch(initUrl);

        if (!res.ok) {
          throw new Error(res.statusText);
        }

        const siteConfig: TenantData = await res.json();
        setTenantData(siteConfig);
        setLoading(false);
      } catch (err) {
        log('Failed to fetch tenant data:', err);
        setLoading(false);
      }
    };

    void fetchTenantData();
  }, []);

  // Set feature flags
  useEffect(() => {
    if (tenantData) {
      // Get feature keys dynamically from featuresDefaults (single source of truth for feature keys)
      const featureKeys = Object.keys(featuresDefaults) as Array<keyof Features>;
      featureKeys.forEach((key) => {
        const tenantFeatureValue = tenantData.features[key];
        // Use tenantData.features value if provided, otherwise fall back to default
        useFeatureStore
          .getState()
          .setFeature(key, typeof tenantFeatureValue === 'boolean' ? tenantFeatureValue : featuresDefaults[key]);
      });
    }
  }, [tenantData]);

  const renderContents = () => {
    if (loading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-white dark:bg-neutral-950">
          <Loading />
        </div>
      );
    } else if (redirecting) {
      return (
        <div className="flex min-h-screen items-center justify-center  bg-neutral-950 text-white">
          <div className="flex flex-col items-center gap-5 rounded-lg bg-yellow-500/10 p-10">
            {i18nMessages.common.redirecting}
          </div>
        </div>
      );
    } else if (!tenantData) {
      return (
        <NotFound
          title={i18nMessages.errors.tenantNotFound}
          icon={<AlertTriangle className="mr-2 icon-12 stroke-yellow-500" strokeWidth={1} />}
        />
      );
    } else if (isPublicRoute) {
      return (
        <Auth0Provider
          {...buildAuth0ProviderProps(tenantData)}
          onRedirectCallback={(appState?: AppState) => {
            void Router.replace(appState?.returnTo ? sanitizeRedirectUrl(appState.returnTo) : '/');
          }}
        >
          <QueryClientProvider client={queryClient}>
            <HistoryProvider>
              <TooltipProvider delayDuration={100}>
                <DragAndDropProvider>
                  <GallerySelectionProvider>
                    <CommandContextProvider>
                      <Component {...pageProps} />
                      <ModalHost />
                    </CommandContextProvider>
                    <Toaster />
                    <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
                  </GallerySelectionProvider>
                </DragAndDropProvider>
              </TooltipProvider>
            </HistoryProvider>
          </QueryClientProvider>
          <div id="portal" />
        </Auth0Provider>
      );
    } else {
      return (
        <Auth0Provider
          {...buildAuth0ProviderProps(tenantData)}
          onRedirectCallback={(appState?: AppState) => {
            void Router.replace(appState?.returnTo ?? '/');
          }}
        >
          <GlobalErrorProvider>
            <QueryClientProvider client={queryClient}>
              <AuthWall>
                <GuestWall>
                  <TermsAcceptanceGuard>
                    <HistoryProvider>
                      <TooltipProvider delayDuration={100}>
                        <GallerySelectionProvider>
                          <TreeContextProvider>
                            <TypesenseProvider>
                              <DragAndDropProvider>
                                <CommandContextProvider>
                                  <GlobalErrorHandler>
                                    <GatedExportProgressManager />
                                    <DuplicateProgressManager />
                                    <Component {...pageProps} tenant={tenantData.tenant} />
                                    <ModalHost />
                                  </GlobalErrorHandler>
                                </CommandContextProvider>
                              </DragAndDropProvider>
                              <Toaster />
                              <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
                            </TypesenseProvider>
                          </TreeContextProvider>
                        </GallerySelectionProvider>
                      </TooltipProvider>
                    </HistoryProvider>
                  </TermsAcceptanceGuard>
                  <div id="portal" />
                </GuestWall>
              </AuthWall>
            </QueryClientProvider>
          </GlobalErrorProvider>
        </Auth0Provider>
      );
    }
  };

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0" />
      </Head>
      <ThumbnailSizeCssVar />
      <AppI18nProvider>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <AuthErrorWall>{renderContents()}</AuthErrorWall>
        </ThemeProvider>
      </AppI18nProvider>
    </>
  );
}
